"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AltiumShapeConverters = void 0;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const AltiumEnums_1 = require("../../common/AltiumEnums");
const layers_1 = require("../../../layers");
const CoordinateConverter_1 = require("../parse/CoordinateConverter");
const PropertyParser_1 = require("../parse/PropertyParser");
class AltiumShapeConverters {
    static { this.LineWidthFactor = 10; }
    static { this.PinDesignatorBodyInset = CoordinateConverter_1.CoordinateConverter.milsToMm(50); }
    static { this.HarnessBraceMaximumDepth = CoordinateConverter_1.CoordinateConverter.milsToMm(200); }
    static { this.HarnessBraceArcSampleCount = 6; }
    static { this.HarnessEntryInset = CoordinateConverter_1.CoordinateConverter.milsToMm(50); }
    static { this.HarnessEntryMarkerRadius = CoordinateConverter_1.CoordinateConverter.milsToMm(7); }
    static { this.Logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)(); }
    static convertLine(record, invertY = false) {
        const ySign = invertY ? -1 : 1;
        return {
            type: 'segment',
            start: [
                CoordinateConverter_1.CoordinateConverter.milsToMm(record.startLocation.x),
                ySign * CoordinateConverter_1.CoordinateConverter.milsToMm(record.startLocation.y),
            ],
            end: [
                CoordinateConverter_1.CoordinateConverter.milsToMm(record.endLocation.x),
                ySign * CoordinateConverter_1.CoordinateConverter.milsToMm(record.endLocation.y),
            ],
            width: CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * AltiumShapeConverters.LineWidthFactor,
        };
    }
    static convertRectangle(record, invertY = false) {
        const ySign = invertY ? -1 : 1;
        return {
            type: 'rect',
            start: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.topLeft.x), ySign * CoordinateConverter_1.CoordinateConverter.milsToMm(record.topLeft.y)],
            end: [
                CoordinateConverter_1.CoordinateConverter.milsToMm(record.bottomRight.x),
                ySign * CoordinateConverter_1.CoordinateConverter.milsToMm(record.bottomRight.y),
            ],
            strokeWidth: CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * 10,
            filled: record.isSolid,
        };
    }
    static convertToColorOverride(fillColor) {
        return [fillColor.r, fillColor.g, fillColor.b, 1];
    }
    static convertPolyline(record, invertY = false) {
        const ySign = invertY ? -1 : 1;
        return {
            type: 'polygon',
            points: record.points.map((p) => [CoordinateConverter_1.CoordinateConverter.milsToMm(p.x), ySign * CoordinateConverter_1.CoordinateConverter.milsToMm(p.y)]),
            filled: false,
            width: CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * AltiumShapeConverters.LineWidthFactor,
        };
    }
    static convertBus(record) {
        const points = record.points.map((p) => [
            CoordinateConverter_1.CoordinateConverter.milsToMm(p.x),
            CoordinateConverter_1.CoordinateConverter.milsToMm(p.y),
        ]);
        const buses = [];
        const lineWidth = CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * AltiumShapeConverters.LineWidthFactor * 2;
        for (let i = 0; i < points.length - 1; i++) {
            buses.push({
                type: 'bus',
                start: points[i],
                end: points[i + 1],
                width: lineWidth,
            });
        }
        return buses;
    }
    static convertSignalHarness(record) {
        const points = record.points.map((p) => [
            CoordinateConverter_1.CoordinateConverter.milsToMm(p.x),
            CoordinateConverter_1.CoordinateConverter.milsToMm(p.y),
        ]);
        const harnesses = [];
        const lineWidth = CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * AltiumShapeConverters.LineWidthFactor * 2;
        for (let i = 0; i < points.length - 1; i++) {
            harnesses.push({
                type: 'bus',
                start: points[i],
                end: points[i + 1],
                width: lineWidth,
                colorOverride: AltiumShapeConverters.convertToColorOverride(record.color),
            });
        }
        return harnesses;
    }
    static convertHarnessConnector(record) {
        if (!Number.isFinite(record.xSize) || record.xSize <= 0 || !Number.isFinite(record.ySize) || record.ySize <= 0) {
            return undefined;
        }
        const x = CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.x);
        const y = CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.y);
        const width = CoordinateConverter_1.CoordinateConverter.milsToMm(record.xSize);
        const height = CoordinateConverter_1.CoordinateConverter.milsToMm(record.ySize);
        const sideGeometry = AltiumShapeConverters.getHarnessSideGeometry(record.side);
        const rawPrimaryPosition = CoordinateConverter_1.CoordinateConverter.milsToMm(record.primaryConnectionPosition);
        const primaryPosition = Number.isFinite(rawPrimaryPosition) ? rawPrimaryPosition : (sideGeometry.isVerticalSide ? height : width) / 2;
        const braceDepth = AltiumShapeConverters.calculateHarnessBraceDepth(record);
        let firstInnerCorner;
        let secondInnerCorner;
        let farCorners;
        const tip = [
            x + sideGeometry.outerEdgeRatio[0] * width + sideGeometry.tangent[0] * primaryPosition,
            y + sideGeometry.outerEdgeRatio[1] * height + sideGeometry.tangent[1] * primaryPosition,
        ];
        switch (record.side) {
            case AltiumEnums_1.SheetSymbolSide.Right: {
                const innerX = x + width - braceDepth;
                firstInnerCorner = [innerX, y];
                secondInnerCorner = [innerX, y + height];
                farCorners = [
                    [x, y],
                    [x, y + height],
                ];
                break;
            }
            case AltiumEnums_1.SheetSymbolSide.Top: {
                const innerY = y + braceDepth;
                firstInnerCorner = [x, innerY];
                secondInnerCorner = [x + width, innerY];
                farCorners = [
                    [x, y + height],
                    [x + width, y + height],
                ];
                break;
            }
            case AltiumEnums_1.SheetSymbolSide.Bottom: {
                const innerY = y + height - braceDepth;
                firstInnerCorner = [x, innerY];
                secondInnerCorner = [x + width, innerY];
                farCorners = [
                    [x, y],
                    [x + width, y],
                ];
                break;
            }
            case AltiumEnums_1.SheetSymbolSide.Left:
            default: {
                const innerX = x + braceDepth;
                firstInnerCorner = [innerX, y];
                secondInnerCorner = [innerX, y + height];
                farCorners = [
                    [x + width, y],
                    [x + width, y + height],
                ];
                break;
            }
        }
        const colorOverride = AltiumShapeConverters.convertToColorOverride(record.color);
        const braceWidth = CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * AltiumShapeConverters.LineWidthFactor;
        const firstArm = AltiumShapeConverters.sampleHarnessBraceArm(firstInnerCorner, tip, record.side, braceDepth);
        const secondArm = AltiumShapeConverters.sampleHarnessBraceArm(secondInnerCorner, tip, record.side, braceDepth);
        const drawings = [];
        if (record.isSolid) {
            drawings.push({
                type: 'polygon',
                points: [farCorners[0], ...firstArm, ...secondArm.toReversed(), farCorners[1]],
                filled: true,
                width: 0,
                colorOverride: AltiumShapeConverters.convertToColorOverride(record.areaColor),
            });
        }
        drawings.push({
            type: 'polygon',
            points: firstArm,
            filled: false,
            width: braceWidth,
            colorOverride,
        }, {
            type: 'polygon',
            points: secondArm,
            filled: false,
            width: braceWidth,
            colorOverride,
        });
        return { type: 'group', pos: [0, 0], drawings };
    }
    static convertHarnessEntryText(record, entryPosition, sheet, projectFontId, options) {
        if (record.name.length === 0) {
            return undefined;
        }
        const sideGeometry = AltiumShapeConverters.getHarnessSideGeometry(record.side);
        const pos = [
            entryPosition[0] - sideGeometry.outward[0] * AltiumShapeConverters.HarnessEntryInset,
            entryPosition[1] - sideGeometry.outward[1] * AltiumShapeConverters.HarnessEntryInset,
        ];
        const justifyDirection = sideGeometry.isVerticalSide ? sideGeometry.outward[0] : -sideGeometry.outward[1];
        const justify = [justifyDirection, 0];
        const angle = sideGeometry.isVerticalSide ? 0 : 90;
        const resolvedFont = AltiumShapeConverters.resolveHarnessFont(sheet, record.textFontId, projectFontId, options);
        return PropertyParser_1.PropertyParser.parseAltiumTextTransforms({
            type: 'text',
            pos,
            value: record.name,
            angle,
            thickness: 1,
            justify,
            ...resolvedFont.fontInfo.textOpts,
            font: resolvedFont.projectFontId,
            colorOverride: AltiumShapeConverters.convertToColorOverride(record.textColor),
        });
    }
    static convertHarnessEntryMarker(record, entryPosition) {
        return {
            type: 'circle',
            center: entryPosition,
            radius: AltiumShapeConverters.HarnessEntryMarkerRadius,
            filled: true,
            colorOverride: AltiumShapeConverters.convertToColorOverride(record.color),
        };
    }
    static convertHarnessType(record, fallbackPosition, sheet, projectFontId, options) {
        if (record.isHidden) {
            return undefined;
        }
        const hasStoredLocation = record.properties['location.x'] !== undefined && record.properties['location.y'] !== undefined;
        const pos = hasStoredLocation ?
            [CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.x), CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.y)]
            : fallbackPosition;
        const resolvedFont = AltiumShapeConverters.resolveHarnessFont(sheet, record.fontId, projectFontId, options);
        return PropertyParser_1.PropertyParser.parseAltiumTextTransforms({
            type: 'text',
            pos,
            value: record.text,
            thickness: 1,
            justify: [-1, 1],
            ...resolvedFont.fontInfo.textOpts,
            font: resolvedFont.projectFontId,
        });
    }
    static calculateHarnessBraceDepth(record) {
        const width = CoordinateConverter_1.CoordinateConverter.milsToMm(record.xSize);
        const height = CoordinateConverter_1.CoordinateConverter.milsToMm(record.ySize);
        const { isVerticalSide } = AltiumShapeConverters.getHarnessSideGeometry(record.side);
        const sideSpan = isVerticalSide ? height : width;
        const bodyDepth = isVerticalSide ? width : height;
        return Math.min(sideSpan / 2, AltiumShapeConverters.HarnessBraceMaximumDepth, bodyDepth);
    }
    static getHarnessSideGeometry(side) {
        switch (side) {
            case AltiumEnums_1.SheetSymbolSide.Right:
                return { isVerticalSide: true, outerEdgeRatio: [1, 0], outward: [1, 0], tangent: [0, 1] };
            case AltiumEnums_1.SheetSymbolSide.Top:
                return { isVerticalSide: false, outerEdgeRatio: [0, 0], outward: [0, -1], tangent: [1, 0] };
            case AltiumEnums_1.SheetSymbolSide.Bottom:
                return { isVerticalSide: false, outerEdgeRatio: [0, 1], outward: [0, 1], tangent: [1, 0] };
            case AltiumEnums_1.SheetSymbolSide.Left:
            default:
                return { isVerticalSide: true, outerEdgeRatio: [0, 0], outward: [-1, 0], tangent: [0, 1] };
        }
    }
    static sampleHarnessBraceArm(start, end, side, braceDepth) {
        const { outward, tangent } = AltiumShapeConverters.getHarnessSideGeometry(side);
        const tangentSpan = (end[0] - start[0]) * tangent[0] + (end[1] - start[1]) * tangent[1];
        const points = [];
        for (let i = 0; i <= AltiumShapeConverters.HarnessBraceArcSampleCount; i++) {
            const angle = (i / AltiumShapeConverters.HarnessBraceArcSampleCount) * (Math.PI / 2);
            points.push(AltiumShapeConverters.calculateHarnessBracePoint(start, outward, tangent, (braceDepth / 2) * Math.sin(angle), (tangentSpan / 2) * (1 - Math.cos(angle))));
        }
        for (let i = 1; i <= AltiumShapeConverters.HarnessBraceArcSampleCount; i++) {
            const angle = (i / AltiumShapeConverters.HarnessBraceArcSampleCount) * (Math.PI / 2);
            points.push(AltiumShapeConverters.calculateHarnessBracePoint(start, outward, tangent, braceDepth / 2 + (braceDepth / 2) * (1 - Math.cos(angle)), tangentSpan / 2 + (tangentSpan / 2) * Math.sin(angle)));
        }
        return points;
    }
    static calculateHarnessBracePoint(start, outward, tangent, normalOffset, tangentOffset) {
        return [
            start[0] + outward[0] * normalOffset + tangent[0] * tangentOffset,
            start[1] + outward[1] * normalOffset + tangent[1] * tangentOffset,
        ];
    }
    static resolveHarnessFont(sheet, fontId, projectFontId, options) {
        if (fontId === 0) {
            return { fontInfo: sheet.getFont(0), projectFontId: options.defaultProjectFontId };
        }
        if (!sheet.getFonts().has(fontId)) {
            if (!options.reportedMissingFontIds.has(fontId)) {
                AltiumShapeConverters.Logger.warn('ResolveHarnessFont_FontNotFound', { fontId });
                options.reportedMissingFontIds.add(fontId);
            }
            return { fontInfo: sheet.getFont(0), projectFontId: options.defaultProjectFontId };
        }
        return { fontInfo: sheet.getFont(fontId), projectFontId };
    }
    static convertBusEntry(record) {
        return {
            type: 'track',
            start: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.x), CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.y)],
            end: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.corner.x), CoordinateConverter_1.CoordinateConverter.milsToMm(record.corner.y)],
            width: 0.254,
        };
    }
    static convertPolygon(record, invertY = false) {
        const ySign = invertY ? -1 : 1;
        return {
            type: 'polygon',
            points: record.points.map((p) => [CoordinateConverter_1.CoordinateConverter.milsToMm(p.x), ySign * CoordinateConverter_1.CoordinateConverter.milsToMm(p.y)]),
            filled: record.isSolid,
            width: CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * AltiumShapeConverters.LineWidthFactor,
        };
    }
    static convertArc(record, invertY = false) {
        const ySign = invertY ? -1 : 1;
        let startAngle = -record.endAngle;
        let endAngle = -record.startAngle;
        startAngle = sigma_engine_shared_1.Utils.Transform.clampRotation(startAngle);
        endAngle = sigma_engine_shared_1.Utils.Transform.clampRotation(endAngle);
        if (startAngle > endAngle) {
            endAngle += 360;
        }
        if (Math.abs(startAngle - endAngle) < 0.001) {
            return {
                type: 'circle',
                center: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.center.x), ySign * CoordinateConverter_1.CoordinateConverter.milsToMm(record.center.y)],
                radius: CoordinateConverter_1.CoordinateConverter.milsToMm(record.radius),
                filled: record.isSolid,
                width: CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * AltiumShapeConverters.LineWidthFactor,
            };
        }
        return {
            type: 'arc',
            center: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.center.x), ySign * CoordinateConverter_1.CoordinateConverter.milsToMm(record.center.y)],
            radius: CoordinateConverter_1.CoordinateConverter.milsToMm(record.radius),
            endAngle,
            startAngle,
            lineWidth: CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * AltiumShapeConverters.LineWidthFactor,
        };
    }
    static convertBezier(record, invertY = false) {
        if (record.points.length === 0) {
            throw new Error('Bezier curve must have at least 1 point');
        }
        const ySign = invertY ? -1 : 1;
        const toMm = (point) => [
            CoordinateConverter_1.CoordinateConverter.milsToMm(point.x),
            ySign * CoordinateConverter_1.CoordinateConverter.milsToMm(point.y),
        ];
        const lerp = (a, b, t) => ({
            x: a.x + (b.x - a.x) * t,
            y: a.y + (b.y - a.y) * t,
        });
        const [start, ...rest] = record.points;
        const end = record.points.length >= 4 ? record.points[3] : (rest[rest.length - 1] ?? start);
        let cpa = start;
        let cpb = end;
        if (record.points.length === 3) {
            cpa = lerp(start, record.points[1], 2 / 3);
            cpb = lerp(end, record.points[1], 2 / 3);
        }
        else if (record.points.length >= 4) {
            cpa = record.points[1];
            cpb = record.points[2];
        }
        return {
            type: 'curve',
            start: toMm(start),
            cpa: toMm(cpa),
            cpb: toMm(cpb),
            end: toMm(end),
            width: CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * AltiumShapeConverters.LineWidthFactor,
        };
    }
    static convertEllipse(record, invertY = false) {
        const ySign = invertY ? -1 : 1;
        if (record.radius === record.secondaryRadius) {
            return {
                type: 'circle',
                center: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.center.x), ySign * CoordinateConverter_1.CoordinateConverter.milsToMm(record.center.y)],
                radius: CoordinateConverter_1.CoordinateConverter.milsToMm(record.radius),
                filled: record.isSolid,
                width: CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * AltiumShapeConverters.LineWidthFactor,
            };
        }
        return {
            type: 'circle',
            center: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.center.x), ySign * CoordinateConverter_1.CoordinateConverter.milsToMm(record.center.y)],
            radius: CoordinateConverter_1.CoordinateConverter.milsToMm(record.radius),
            filled: record.isSolid,
            width: CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * AltiumShapeConverters.LineWidthFactor,
        };
    }
    static convertEllipticalArc(record, invertY = false) {
        const ySign = invertY ? -1 : 1;
        if (record.radius === record.secondaryRadius) {
            return {
                type: 'arc',
                center: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.center.x), ySign * CoordinateConverter_1.CoordinateConverter.milsToMm(record.center.y)],
                radius: CoordinateConverter_1.CoordinateConverter.milsToMm(record.radius),
                startAngle: record.startAngle,
                endAngle: record.endAngle,
                lineWidth: CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * AltiumShapeConverters.LineWidthFactor,
            };
        }
        return {
            type: 'arc',
            center: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.center.x), ySign * CoordinateConverter_1.CoordinateConverter.milsToMm(record.center.y)],
            radius: CoordinateConverter_1.CoordinateConverter.milsToMm(record.radius),
            startAngle: record.startAngle,
            endAngle: record.endAngle,
            lineWidth: CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * AltiumShapeConverters.LineWidthFactor,
        };
    }
    static convertRoundRectangle(record, invertY = false) {
        const ySign = invertY ? -1 : 1;
        return {
            type: 'rect',
            start: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.x), ySign * CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.y)],
            end: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.corner.x), ySign * CoordinateConverter_1.CoordinateConverter.milsToMm(record.corner.y)],
            strokeWidth: CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * AltiumShapeConverters.LineWidthFactor,
            filled: record.isSolid,
        };
    }
    static convertPin(record, componentOrientation = AltiumEnums_1.Orientation.Rotate0) {
        const pinModel = {
            type: 'pin',
            ref: record.designator,
            name: record.name,
            number: record.designator,
            start: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.x), CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.y)],
            end: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.x), CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.y)],
            layer: (0, layers_1.getAltiumSchLayerByRecordType)(record.recordType).id,
            width: 0.254,
            ...(record.hidden ? { hidden: true } : {}),
        };
        const pinLengthMm = -CoordinateConverter_1.CoordinateConverter.milsToMm(record.pinLength) * 10;
        switch (record.orientation) {
            case AltiumEnums_1.Orientation.Rotate0:
                pinModel.start[0] += -pinLengthMm;
                break;
            case AltiumEnums_1.Orientation.Rotate90:
                pinModel.start[1] += pinLengthMm;
                break;
            case AltiumEnums_1.Orientation.Rotate180:
                pinModel.start[0] -= -pinLengthMm;
                break;
            case AltiumEnums_1.Orientation.Rotate270:
                pinModel.start[1] -= pinLengthMm;
                break;
        }
        const horizontalAngle = sigma_engine_shared_1.Utils.Transform.clampRotation((componentOrientation - 1) * 90);
        const verticalAngle = sigma_engine_shared_1.Utils.Transform.clampRotation(componentOrientation * 90);
        let designatorOffset;
        let designatorBodyInset;
        let designatorAnchor = pinModel.end;
        let designatorJustify;
        let designatorAngle;
        let nameOffset;
        let nameJustify;
        let nameAngle;
        switch (record.orientation) {
            case AltiumEnums_1.Orientation.Rotate0:
                designatorOffset = [0, -0.5];
                designatorBodyInset = [AltiumShapeConverters.PinDesignatorBodyInset, 0];
                designatorAngle = horizontalAngle;
                designatorJustify = [1, 1];
                nameOffset = [-1, 0];
                nameAngle = horizontalAngle;
                nameJustify = [1, 0];
                break;
            case AltiumEnums_1.Orientation.Rotate90:
                designatorOffset = [-0.5, 0];
                designatorBodyInset = [0, -AltiumShapeConverters.PinDesignatorBodyInset];
                designatorAngle = verticalAngle;
                designatorJustify = [1, 1];
                nameOffset = [0, -1];
                nameAngle = verticalAngle;
                nameJustify = [0, 1];
                break;
            case AltiumEnums_1.Orientation.Rotate180:
                designatorOffset = [0, -0.5];
                designatorBodyInset = [-AltiumShapeConverters.PinDesignatorBodyInset, 0];
                designatorAngle = horizontalAngle;
                designatorJustify = [-1, 1];
                nameOffset = [1, 0];
                nameAngle = horizontalAngle;
                nameJustify = [-1, 0];
                break;
            case AltiumEnums_1.Orientation.Rotate270:
                designatorOffset = [0.5, 0];
                designatorBodyInset = [0, AltiumShapeConverters.PinDesignatorBodyInset];
                designatorAngle = verticalAngle;
                designatorJustify = [-1, -1];
                nameOffset = [0, 1];
                nameAngle = verticalAngle;
                nameJustify = [0, -1];
                break;
        }
        if (record.name !== record.designator) {
            designatorAnchor = [(pinModel.start[0] + pinModel.end[0]) / 2, (pinModel.start[1] + pinModel.end[1]) / 2];
            designatorBodyInset = [0, 0];
            designatorJustify = [0, designatorJustify[1]];
        }
        if (record.showDesignator) {
            pinModel.drawings ??= [];
            pinModel.drawings.push({
                layers: [pinModel.layer],
                drawing: {
                    type: 'text',
                    ...PropertyParser_1.PropertyParser.encodeAltiumDisplayText(record.designator),
                    pos: [
                        designatorAnchor[0] + designatorOffset[0] + designatorBodyInset[0],
                        designatorAnchor[1] + designatorOffset[1] + designatorBodyInset[1],
                    ],
                    angle: designatorAngle,
                    justify: designatorJustify,
                    size: 1,
                    thickness: 1,
                },
            });
        }
        if (record.showPinName) {
            pinModel.drawings ??= [];
            pinModel.drawings.push({
                layers: [pinModel.layer],
                drawing: {
                    type: 'text',
                    ...PropertyParser_1.PropertyParser.encodeAltiumDisplayText(record.name),
                    pos: [pinModel.end[0] + nameOffset[0], pinModel.end[1] + nameOffset[1]],
                    angle: nameAngle,
                    justify: nameJustify,
                    size: 1,
                    thickness: 1,
                },
            });
        }
        return pinModel;
    }
    static snapPositionToGrid(position, snapGridSizeMils) {
        const literalMils = [
            CoordinateConverter_1.CoordinateConverter.mmToMils(position[0]),
            CoordinateConverter_1.CoordinateConverter.mmToMils(position[1]),
        ];
        const snapAxis = (value) => Math.round(value / snapGridSizeMils) * snapGridSizeMils;
        const snappedMils = [snapAxis(literalMils[0]), snapAxis(literalMils[1])];
        const identicalToleranceMils = 1e-6;
        if (Math.abs(snappedMils[0] - literalMils[0]) < identicalToleranceMils &&
            Math.abs(snappedMils[1] - literalMils[1]) < identicalToleranceMils) {
            return undefined;
        }
        return [CoordinateConverter_1.CoordinateConverter.milsToMm(snappedMils[0]), CoordinateConverter_1.CoordinateConverter.milsToMm(snappedMils[1])];
    }
    static convertPowerPort(record) {
        const shapes = [];
        const lineWidth = CoordinateConverter_1.CoordinateConverter.milsToMm(10);
        const thinLineWidth = CoordinateConverter_1.CoordinateConverter.milsToMm(5);
        let labelYOffsetMils = -260;
        const m = (mils) => CoordinateConverter_1.CoordinateConverter.milsToMm(mils);
        const addSegment = (x1, y1, x2, y2, width = lineWidth) => {
            shapes.push({
                type: 'segment',
                start: [m(x1), -m(y1)],
                end: [m(x2), -m(y2)],
                width,
            });
        };
        const addPolygon = (points, filled = false, width = lineWidth) => {
            shapes.push({
                type: 'polygon',
                points: points.map(([x, y]) => [m(x), -m(y)]),
                filled,
                width,
            });
        };
        const addCircle = (cx, cy, radius, filled = false, width = thinLineWidth) => {
            shapes.push({
                type: 'circle',
                center: [m(cx), -m(cy)],
                radius: m(radius),
                filled,
                width,
            });
        };
        const addBezier = (startX, startY, cp1X, cp1Y, cp2X, cp2Y, endX, endY, width = thinLineWidth) => {
            shapes.push({
                type: 'curve',
                start: [m(startX), -m(startY)],
                cpa: [m(cp1X), -m(cp1Y)],
                cpb: [m(cp2X), -m(cp2Y)],
                end: [m(endX), -m(endY)],
                width,
            });
        };
        if (record.isCrossSheetConnector) {
            const arrowWidth = 50;
            const arrowSpacing = 5;
            const arrowHeight = 45;
            const offset = 2.54 * 7;
            const style = record.style;
            switch (style) {
                case AltiumEnums_1.OffSheetConnectorStyle.LeftArrow:
                    addPolygon([
                        [offset + arrowWidth, -arrowHeight],
                        [offset + 0, 0],
                        [offset + arrowWidth, arrowHeight],
                    ]);
                    addPolygon([
                        [offset + arrowWidth * 2 + arrowSpacing, -arrowHeight],
                        [offset + 0 + arrowSpacing + arrowWidth, 0],
                        [offset + arrowWidth * 2 + arrowSpacing, arrowHeight],
                    ]);
                    labelYOffsetMils = -(offset * 1.5 + arrowWidth * 2 + arrowSpacing * 2);
                    break;
                case AltiumEnums_1.OffSheetConnectorStyle.RightArrow:
                    addSegment(0, 0, arrowWidth + offset * 1.5 - 2 * 2.54, 0);
                    addPolygon([
                        [-offset * 1.5 + arrowWidth, -arrowHeight],
                        [-offset * 1.5 + arrowWidth + arrowWidth, 0],
                        [-offset * 1.5 + arrowWidth, arrowHeight],
                    ]);
                    addPolygon([
                        [-offset * 1.5 + arrowWidth + arrowSpacing + arrowWidth, -arrowHeight],
                        [-offset * 1.5 + arrowWidth + arrowSpacing + arrowWidth + arrowWidth, 0],
                        [-offset * 1.5 + arrowWidth + arrowSpacing + arrowWidth, arrowHeight],
                    ]);
                    labelYOffsetMils = -(offset * 2 + arrowWidth * 2 + arrowSpacing * 2);
                    break;
                default:
                    throw new Error('Unsupported off-sheet connector style');
            }
        }
        else {
            switch (record.style) {
                case AltiumEnums_1.PowerPortStyle.Circle:
                    addSegment(0, 0, 0, 50);
                    addCircle(0, 75, 25);
                    labelYOffsetMils = -180;
                    break;
                case AltiumEnums_1.PowerPortStyle.Arrow:
                    addSegment(0, 0, 0, 50);
                    addPolygon([
                        [-25, 50],
                        [25, 50],
                        [0, 100],
                        [-25, 50],
                    ]);
                    labelYOffsetMils = -180;
                    break;
                case AltiumEnums_1.PowerPortStyle.Wave:
                    addSegment(0, 0, 0, 72);
                    addBezier(30, 50, 30, 87, -30, 63, -30, 100);
                    labelYOffsetMils = -180;
                    break;
                case AltiumEnums_1.PowerPortStyle.PowerGround:
                    addSegment(0, 0, 0, 100);
                    addSegment(-100, 100, 100, 100);
                    addSegment(-70, 130, 70, 130);
                    addSegment(-40, 160, 40, 160);
                    addSegment(-10, 190, 10, 190);
                    break;
                case AltiumEnums_1.PowerPortStyle.SignalGround:
                    addSegment(0, 0, 0, 100);
                    addPolygon([
                        [-100, 100],
                        [100, 100],
                        [0, 200],
                        [-100, 100],
                    ]);
                    break;
                case AltiumEnums_1.PowerPortStyle.Earth:
                    addSegment(0, 0, 0, 100);
                    addPolygon([
                        [-150, 200],
                        [-100, 100],
                        [100, 100],
                        [50, 200],
                    ]);
                    addSegment(0, 100, -50, 200);
                    break;
                case AltiumEnums_1.PowerPortStyle.GostArrow:
                    addSegment(0, 0, 0, 100);
                    addPolygon([
                        [-25, 50],
                        [0, 100],
                        [25, 50],
                    ]);
                    labelYOffsetMils = -180;
                    break;
                case AltiumEnums_1.PowerPortStyle.GostPowerGround:
                    addSegment(0, 0, 0, 160);
                    addSegment(-100, 160, 100, 160);
                    addSegment(-60, 200, 60, 200);
                    addSegment(-20, 240, 20, 240);
                    labelYOffsetMils = -310;
                    break;
                case AltiumEnums_1.PowerPortStyle.GostEarth:
                    addSegment(0, 0, 0, 160);
                    addSegment(-100, 160, 100, 160);
                    addSegment(-60, 200, 60, 200);
                    addSegment(-20, 240, 20, 240);
                    addCircle(0, 160, 120);
                    labelYOffsetMils = -340;
                    break;
                case AltiumEnums_1.PowerPortStyle.GostBar:
                    addSegment(0, 0, 0, 200);
                    addSegment(-100, 200, 100, 200);
                    break;
                case AltiumEnums_1.PowerPortStyle.Bar:
                default:
                    addSegment(0, 0, 0, 100);
                    addSegment(-50, 100, 50, 100);
                    labelYOffsetMils = -180;
                    break;
            }
        }
        return { graphics: shapes, labelYOffset: CoordinateConverter_1.CoordinateConverter.milsToMm(labelYOffsetMils) };
    }
    static convertWire(record) {
        const tracks = [];
        for (let i = 0; i < record.points.length - 1; i++) {
            tracks.push({
                type: 'track',
                start: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.points[i].x), CoordinateConverter_1.CoordinateConverter.milsToMm(record.points[i].y)],
                end: [
                    CoordinateConverter_1.CoordinateConverter.milsToMm(record.points[i + 1].x),
                    CoordinateConverter_1.CoordinateConverter.milsToMm(record.points[i + 1].y),
                ],
                width: CoordinateConverter_1.CoordinateConverter.milsToMm(record.lineWidth) * AltiumShapeConverters.LineWidthFactor,
            });
        }
        return tracks;
    }
    static convertJunction(record) {
        return {
            type: 'junction',
            position: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.x), CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.y)],
        };
    }
    static convertNetLabel(record, sheet, projectFontId) {
        const text = {
            type: 'text',
            pos: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.x), CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.y)],
            value: record.text,
            angle: record.orientation * -90,
            thickness: 1,
            justify: CoordinateConverter_1.CoordinateConverter.convertJustification(record.justification),
            ...sheet.getFont(record.fontId).textOpts,
            font: projectFontId,
        };
        return PropertyParser_1.PropertyParser.parseAltiumTextTransforms(text);
    }
    static convertLabel(record, sheet, projectFontId) {
        const text = {
            type: 'text',
            pos: [CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.x), -CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.y)],
            value: record.text,
            angle: record.orientation * -90,
            thickness: 1,
            justify: CoordinateConverter_1.CoordinateConverter.convertJustification(record.justification),
            ...sheet.getFont(record.fontId).textOpts,
            font: projectFontId,
        };
        return PropertyParser_1.PropertyParser.parseAltiumTextTransforms(text);
    }
    static convertTextFrame(record, sheet, projectFontId, invertY = false) {
        const locX = CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.x);
        const locY = (invertY ? -1 : 1) * CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.y);
        const cornerX = CoordinateConverter_1.CoordinateConverter.milsToMm(record.corner.x);
        const cornerY = (invertY ? -1 : 1) * CoordinateConverter_1.CoordinateConverter.milsToMm(record.corner.y);
        const width = Math.abs(cornerX - locX);
        const height = Math.abs(cornerY - locY);
        const marginMm = CoordinateConverter_1.CoordinateConverter.milsToMm(record.textMargin);
        let textAlign;
        let justify;
        switch (record.alignment) {
            case AltiumEnums_1.TextFrameAlignment.Center:
                textAlign = 'center';
                justify = [0, -1];
                break;
            case AltiumEnums_1.TextFrameAlignment.Right:
                textAlign = 'right';
                justify = [1, -1];
                break;
            case AltiumEnums_1.TextFrameAlignment.Left:
            default:
                textAlign = 'left';
                justify = [-1, -1];
                break;
        }
        const text = PropertyParser_1.PropertyParser.parseAltiumTextTransforms({
            type: 'text',
            pos: [0, 0],
            value: record.text,
            angle: 0,
            thickness: 1,
            justify,
            textAlign,
            bounds: [width, height],
            padding: [marginMm, marginMm],
            ...sheet.getFont(record.fontId).textOpts,
            font: projectFontId,
            wordWrap: record.isWordWrapped,
            clipToBounds: record.clipToRect,
            contrastWithLayer: record.isSolid,
        });
        const drawings = [];
        if (record.showBorder || record.isSolid) {
            const rect = {
                type: 'rect',
                start: [0, 0],
                end: [width, height],
                strokeWidth: CoordinateConverter_1.CoordinateConverter.milsToMm(record.borderWidth) * AltiumShapeConverters.LineWidthFactor,
                filled: record.isSolid,
            };
            drawings.push(rect);
        }
        drawings.push(text);
        const minX = Math.min(locX, cornerX);
        const minY = Math.min(locY, cornerY);
        return {
            type: 'group',
            pos: [minX, minY],
            drawings,
        };
    }
    static convertSheetSymbol(record) {
        const x = CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.x);
        const y = -CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.y);
        const width = CoordinateConverter_1.CoordinateConverter.milsToMm(record.xSize);
        const height = CoordinateConverter_1.CoordinateConverter.milsToMm(record.ySize);
        return {
            type: 'rect',
            start: [x, -y],
            end: [x + width, -(y + height)],
            strokeWidth: CoordinateConverter_1.CoordinateConverter.milsToMm(10),
            filled: record.isSolid,
        };
    }
    static generateSheetEntryIconDrawings(entry, relativePosX, relativePosY) {
        const drawings = [];
        const iconWidth = CoordinateConverter_1.CoordinateConverter.milsToMm(150);
        const iconHeight = CoordinateConverter_1.CoordinateConverter.milsToMm(20 * 4);
        const arrowDepth = CoordinateConverter_1.CoordinateConverter.milsToMm(5 * 8);
        const lineWidth = CoordinateConverter_1.CoordinateConverter.milsToMm(1 * 3);
        if (entry.ioType === AltiumEnums_1.PortIOType.Unspecified) {
            return { drawings, width: 0, height: 0 };
        }
        let points = [];
        if (entry.ioType === AltiumEnums_1.PortIOType.Input) {
            switch (entry.side) {
                case AltiumEnums_1.SheetSymbolSide.Left:
                    points = [
                        [0, -iconHeight / 2],
                        [iconWidth - arrowDepth, -iconHeight / 2],
                        [iconWidth, 0],
                        [iconWidth - arrowDepth, iconHeight / 2],
                        [0, iconHeight / 2],
                        [0, -iconHeight / 2],
                    ];
                    break;
                case AltiumEnums_1.SheetSymbolSide.Right:
                    points = [
                        [0, -iconHeight / 2],
                        [-iconWidth + arrowDepth, -iconHeight / 2],
                        [-iconWidth, 0],
                        [-iconWidth + arrowDepth, iconHeight / 2],
                        [0, iconHeight / 2],
                        [0, -iconHeight / 2],
                    ];
                    break;
                case AltiumEnums_1.SheetSymbolSide.Top:
                    points = [
                        [-iconHeight / 2, 0],
                        [-iconHeight / 2, iconWidth - arrowDepth],
                        [0, iconWidth],
                        [iconHeight / 2, iconWidth - arrowDepth],
                        [iconHeight / 2, 0],
                        [-iconHeight / 2, 0],
                    ];
                    break;
                case AltiumEnums_1.SheetSymbolSide.Bottom:
                    points = [
                        [-iconHeight / 2, 0],
                        [-iconHeight / 2, -iconWidth + arrowDepth],
                        [0, -iconWidth],
                        [iconHeight / 2, -iconWidth + arrowDepth],
                        [iconHeight / 2, 0],
                        [-iconHeight / 2, 0],
                    ];
                    break;
            }
        }
        else if (entry.ioType === AltiumEnums_1.PortIOType.Output) {
            switch (entry.side) {
                case AltiumEnums_1.SheetSymbolSide.Left:
                    points = [
                        [iconWidth, -iconHeight / 2],
                        [arrowDepth, -iconHeight / 2],
                        [0, 0],
                        [arrowDepth, iconHeight / 2],
                        [iconWidth, iconHeight / 2],
                        [iconWidth, -iconHeight / 2],
                    ];
                    break;
                case AltiumEnums_1.SheetSymbolSide.Right:
                    points = [
                        [-iconWidth, -iconHeight / 2],
                        [-arrowDepth, -iconHeight / 2],
                        [0, 0],
                        [-arrowDepth, iconHeight / 2],
                        [-iconWidth, iconHeight / 2],
                        [-iconWidth, -iconHeight / 2],
                    ];
                    break;
                case AltiumEnums_1.SheetSymbolSide.Top:
                    points = [
                        [-iconHeight / 2, iconWidth],
                        [-iconHeight / 2, arrowDepth],
                        [0, 0],
                        [iconHeight / 2, arrowDepth],
                        [iconHeight / 2, iconWidth],
                        [-iconHeight / 2, iconWidth],
                    ];
                    break;
                case AltiumEnums_1.SheetSymbolSide.Bottom:
                    points = [
                        [-iconHeight / 2, -iconWidth],
                        [-iconHeight / 2, -arrowDepth],
                        [0, 0],
                        [iconHeight / 2, -arrowDepth],
                        [iconHeight / 2, -iconWidth],
                        [-iconHeight / 2, -iconWidth],
                    ];
                    break;
            }
        }
        else if (entry.ioType === AltiumEnums_1.PortIOType.Bidirectional) {
            switch (entry.side) {
                case AltiumEnums_1.SheetSymbolSide.Left:
                    points = [
                        [arrowDepth, -iconHeight / 2],
                        [iconWidth - arrowDepth, -iconHeight / 2],
                        [iconWidth, 0],
                        [iconWidth - arrowDepth, iconHeight / 2],
                        [arrowDepth, iconHeight / 2],
                        [0, 0],
                        [arrowDepth, -iconHeight / 2],
                    ];
                    break;
                case AltiumEnums_1.SheetSymbolSide.Right:
                    points = [
                        [-arrowDepth, -iconHeight / 2],
                        [-iconWidth + arrowDepth, -iconHeight / 2],
                        [-iconWidth, 0],
                        [-iconWidth + arrowDepth, iconHeight / 2],
                        [-arrowDepth, iconHeight / 2],
                        [0, 0],
                        [-arrowDepth, -iconHeight / 2],
                    ];
                    break;
                case AltiumEnums_1.SheetSymbolSide.Top:
                    points = [
                        [-iconHeight / 2, arrowDepth],
                        [-iconHeight / 2, iconWidth - arrowDepth],
                        [0, iconWidth],
                        [iconHeight / 2, iconWidth - arrowDepth],
                        [iconHeight / 2, arrowDepth],
                        [0, 0],
                        [-iconHeight / 2, arrowDepth],
                    ];
                    break;
                case AltiumEnums_1.SheetSymbolSide.Bottom:
                    points = [
                        [-iconHeight / 2, -arrowDepth],
                        [-iconHeight / 2, -iconWidth + arrowDepth],
                        [0, -iconWidth],
                        [iconHeight / 2, -iconWidth + arrowDepth],
                        [iconHeight / 2, -arrowDepth],
                        [0, 0],
                        [-iconHeight / 2, -arrowDepth],
                    ];
                    break;
            }
        }
        if (points.length > 0) {
            const polygon = {
                type: 'polygon',
                points,
                filled: true,
                width: lineWidth,
            };
            const translatedPolygon = sigma_engine_shared_1.Utils.Transform.translateDrawing(polygon, [relativePosX, relativePosY]);
            drawings.push(translatedPolygon);
        }
        return { drawings, width: iconWidth, height: iconHeight };
    }
    static calculateSheetEntryPosition(sheetSymbol, entry) {
        const sheetX = CoordinateConverter_1.CoordinateConverter.milsToMm(sheetSymbol.location.x);
        const sheetY = -CoordinateConverter_1.CoordinateConverter.milsToMm(sheetSymbol.location.y);
        const sheetWidth = CoordinateConverter_1.CoordinateConverter.milsToMm(sheetSymbol.xSize);
        const sheetHeight = CoordinateConverter_1.CoordinateConverter.milsToMm(sheetSymbol.ySize);
        const distanceFromTop = CoordinateConverter_1.CoordinateConverter.milsToMm(entry.distanceFromTop) * AltiumShapeConverters.LineWidthFactor;
        switch (entry.side) {
            case AltiumEnums_1.SheetSymbolSide.Left:
                return [sheetX, -sheetY + distanceFromTop];
            case AltiumEnums_1.SheetSymbolSide.Right:
                return [sheetX + sheetWidth, -sheetY + distanceFromTop];
            case AltiumEnums_1.SheetSymbolSide.Top:
                return [sheetX + distanceFromTop, -sheetY];
            case AltiumEnums_1.SheetSymbolSide.Bottom:
                return [sheetX + distanceFromTop, -sheetY + sheetHeight];
            default:
                return [sheetX, -sheetY + distanceFromTop];
        }
    }
    static convertSheetEntryLabel(entry, sheet, position, offset, projectFontId) {
        const labelSpacing = CoordinateConverter_1.CoordinateConverter.milsToMm(40);
        const adjustedPos = [...position];
        let justify = [0, 0];
        switch (entry.side) {
            case AltiumEnums_1.SheetSymbolSide.Left:
                adjustedPos[0] += offset[0] + labelSpacing;
                justify = [-1, 0];
                break;
            case AltiumEnums_1.SheetSymbolSide.Right:
                adjustedPos[0] -= offset[0] + labelSpacing;
                justify = [1, 0];
                break;
            case AltiumEnums_1.SheetSymbolSide.Top:
                adjustedPos[1] += offset[1] + labelSpacing;
                justify = [0, -1];
                break;
            case AltiumEnums_1.SheetSymbolSide.Bottom:
                adjustedPos[1] -= offset[1] + labelSpacing;
                justify = [0, 1];
                break;
        }
        const text = {
            type: 'text',
            pos: adjustedPos,
            value: entry.name,
            angle: 0,
            thickness: 1,
            justify,
            ...sheet.getFont(entry.textFontId).textOpts,
            font: projectFontId,
        };
        return PropertyParser_1.PropertyParser.parseAltiumTextTransforms(text);
    }
    static convertPort(port, sheet, projectFontId) {
        const pos = [
            CoordinateConverter_1.CoordinateConverter.milsToMm(port.location.x),
            CoordinateConverter_1.CoordinateConverter.milsToMm(port.location.y),
        ];
        const isVertical = port.style === AltiumEnums_1.PortStyle.NoneVertical ||
            port.style === AltiumEnums_1.PortStyle.Top ||
            port.style === AltiumEnums_1.PortStyle.Bottom ||
            port.style === AltiumEnums_1.PortStyle.TopBottom;
        const portWidth = CoordinateConverter_1.CoordinateConverter.milsToMm(port.width);
        const portHeight = CoordinateConverter_1.CoordinateConverter.milsToMm(port.height);
        const arrowDepth = portHeight * 0.55;
        const { drawings: shapeDrawings } = this.createPortShape(port, portWidth, portHeight, arrowDepth, isVertical);
        const textLabel = this.createPortText(port, sheet, arrowDepth, isVertical, projectFontId);
        const drawings = [...shapeDrawings, textLabel];
        return {
            type: 'group',
            pos,
            angle: isVertical ? 90 : 0,
            drawings,
        };
    }
    static createPortShape(port, width, height, arrowDepth, _isVertical) {
        const drawings = [];
        const iconWidth = width;
        const iconHeight = height;
        const lineWidth = 0.254;
        let points = [];
        if (port.ioType === AltiumEnums_1.PortIOType.Output) {
            points = [
                [0, -iconHeight / 2],
                [iconWidth - arrowDepth, -iconHeight / 2],
                [iconWidth, 0],
                [iconWidth - arrowDepth, iconHeight / 2],
                [0, iconHeight / 2],
                [0, -iconHeight / 2],
            ];
        }
        else if (port.ioType === AltiumEnums_1.PortIOType.Input) {
            points = [
                [iconWidth, -iconHeight / 2],
                [arrowDepth, -iconHeight / 2],
                [0, 0],
                [arrowDepth, iconHeight / 2],
                [iconWidth, iconHeight / 2],
                [iconWidth, -iconHeight / 2],
            ];
        }
        else if (port.ioType === AltiumEnums_1.PortIOType.Bidirectional) {
            points = [
                [arrowDepth, -iconHeight / 2],
                [iconWidth - arrowDepth, -iconHeight / 2],
                [iconWidth, 0],
                [iconWidth - arrowDepth, iconHeight / 2],
                [arrowDepth, iconHeight / 2],
                [0, 0],
                [arrowDepth, -iconHeight / 2],
            ];
        }
        else {
            points = [
                [0, -iconHeight / 2],
                [iconWidth, -iconHeight / 2],
                [iconWidth, iconHeight / 2],
                [0, iconHeight / 2],
                [0, -iconHeight / 2],
            ];
        }
        if (points.length > 0) {
            const polygon = {
                type: 'polygon',
                points,
                filled: false,
                width: lineWidth,
            };
            drawings.push(polygon);
        }
        return { drawings };
    }
    static createPortText(port, sheet, arrowWidth, _isVertical, projectFontId) {
        const labelSpacing = arrowWidth + CoordinateConverter_1.CoordinateConverter.milsToMm(40);
        const textX = labelSpacing;
        const textY = 0;
        const text = {
            type: 'text',
            pos: [textX, textY],
            value: port.name,
            angle: 0,
            thickness: 1,
            justify: [-1, -0.05],
            ...sheet.getFont(port.fontId).textOpts,
            font: projectFontId,
        };
        return PropertyParser_1.PropertyParser.parseAltiumTextTransforms(text);
    }
    static convertImage(record, imageRef) {
        const x1 = CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.x);
        const y1 = CoordinateConverter_1.CoordinateConverter.milsToMm(record.location.y);
        const x2 = CoordinateConverter_1.CoordinateConverter.milsToMm(record.corner.x);
        const y2 = CoordinateConverter_1.CoordinateConverter.milsToMm(record.corner.y);
        return {
            type: 'image',
            pos: [Math.min(x1, x2), Math.min(y1, y2)],
            width: Math.abs(x2 - x1),
            height: Math.abs(y2 - y1),
            imageRef,
        };
    }
}
exports.AltiumShapeConverters = AltiumShapeConverters;
//# sourceMappingURL=AltiumShapeConverters.js.map